import Dinero from 'dinero.js';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useInvoiceApiV1 } from '../../../../clients/quipli-service/invoice/invoice-api-v1';
import { CanOffRentValidationError } from '../../../../clients/quipli-service/order/order';
import { useOrderApiV1 } from '../../../../clients/quipli-service/order/order-api-v1';
import { Button } from '../../../../components/form/button';
import { Modal } from '../../../../components/modal/modal';
import { Spinner } from '../../../../components/spinner/spinner';
import { ValidationError } from '../../../../components/validation-error/validation-error';
import { TenantContext } from '../../../../context/tenant-context';
import { toast } from '../../../../util/toasts';
import { PartialOffRentStep } from './partial-offrent-step';
import { inventoryReturnCalculations } from '../inventory-return';
import { useHourOverageManagement } from '../shared/hooks/use-hour-overage-management';
import './off-rent.scss';
import { offRentCalculations, toUpdateOrderRequest } from '../shared/utils/off-rent-utils';
import { CalculateFinancialSummaryRequest } from '../../../../clients/quipli-service/financials/financials';
import { safeRound } from '../../../../util/util';
import { useFuelChargeManagement } from '../shared/hooks/use-fuel-charge-management';
import { useOffRentFinancials } from './hooks/use-off-rent-financials';
import { useOffRentRequestManagement } from './hooks/use-off-rent-request-management';
import { useOffRentConfig } from './hooks/use-off-rent-config';
import { OffRentHeader } from './components/off-rent-header';
import { OffRentGridHeader } from './components/off-rent-financial-grid/off-rent-grid-header';
import { OffRentGridProducts } from './components/off-rent-financial-grid/off-rent-grid-products';
import { OffRentAdHocAndOverrages } from './components/off-rent-financial-grid/off-rent-ad-hoc-and-overages';
import { OffRentGridTotal } from './components/off-rent-financial-grid/off-rent-grid-total';
import { OffRentValidationError } from './components/off-rent-financial-grid/off-rent-validation-error';

export const OffRentOrder = ({
  orderId,
  onCancel,
  onComplete,
}: {
  orderId: number;
  onCancel: () => void;
  onComplete: (anyProductsInventoryReturned: boolean) => void;
}) => {
  const { configuration } = useContext(TenantContext);
  const { offRentOrder, inventoryReturn, updateOrder } = useOrderApiV1();
  const { createLineItem } = useInvoiceApiV1();
  const [isOffRenting, setIsOffRenting] = useState<boolean>(false);

  const [existingSummaryRequest, setExistingSummaryRequest] = useState<CalculateFinancialSummaryRequest>();
  const currency = configuration ? (configuration.currency as Dinero.Currency) : undefined;
  const [waiveLateCharge, setWaiveLateCharge] = useState<boolean>(false);
  const [onOffRentUpdateDateCheckbox, setOnOffRentUpdateDateCheckbox] = useState<boolean>(true);
  const [canOffRent, setCanOffRent] = useState<CanOffRentValidationError[]>();

  const {
    unitTrackingType,
    order,
    configs,
    onOffRentUpdateDateConfiguration,
    locations,
    showSelectProductsStep,
    inventoryReturnRequest,
    isLoading,
    setInventoryReturnRequest,
    setShowSelectProductsStep,
    setOrder,
    noProductsToOffRent,
    showInventoryReturn,
  } = useOffRentConfig(orderId);

  const { fuelCharges, hasAnyFuelCharge, setFuelCharges, getFuelChargeForUnit, updateFuelCharge, loading } =
    useFuelChargeManagement(order, showInventoryReturn);

  const { offRentRequest, setOffRentRequest, isPricesLoading, invoice, customPrices } = useOffRentRequestManagement(
    order,
    onOffRentUpdateDateConfiguration,
    setExistingSummaryRequest,
    configs,
  );

  const { existingSummary, setLateChargeSummaryRequest, lateChargeSummary, isFinancialSummaryLoading } =
    useOffRentFinancials(
      order,
      offRentRequest,
      fuelCharges,
      showSelectProductsStep,
      customPrices,
      configs,
      onOffRentUpdateDateCheckbox,
      onOffRentUpdateDateConfiguration,
      setCanOffRent,
      existingSummaryRequest,
      setExistingSummaryRequest,
    );

  const {
    hourOverages,
    hourOverageOverrides,
    isCalculating,
    updateOverageOverride,
    getOverageForUnit,
    getOverrideForUnit,
  } = useHourOverageManagement(inventoryReturnRequest, order);

  // For qu-5647, we want these to be mutually exclusive since there's ambiguity when they're both checked
  useEffect(() => {
    if (waiveLateCharge) {
      setOnOffRentUpdateDateCheckbox(false);
    }
  }, [waiveLateCharge]);

  useEffect(() => {
    if (onOffRentUpdateDateCheckbox) {
      setWaiveLateCharge(false);
    }
  }, [onOffRentUpdateDateCheckbox]);

  const shouldUpdateOrderWithLateChargeSummary = useMemo(() => {
    return (
      (!waiveLateCharge &&
        existingSummary &&
        lateChargeSummary &&
        !existingSummary.total.equalsTo(lateChargeSummary.total) &&
        currency &&
        !order?.isCycleBilled) || // Has a late charge that differs from the existing summary
      (lateChargeSummary && currency && onOffRentUpdateDateConfiguration && onOffRentUpdateDateCheckbox)
      // If the checkbox is checked, always update the order
    );
  }, [
    waiveLateCharge,
    existingSummary,
    lateChargeSummary,
    currency,
    onOffRentUpdateDateConfiguration,
    onOffRentUpdateDateCheckbox,
  ]);

  const shouldUpdateOrderWithExistingSummary = useMemo(() => {
    return existingSummary && currency && hasAnyFuelCharge;
  }, [existingSummary, currency, hasAnyFuelCharge]);

  async function doOffRent() {
    if (offRentRequest && order && (customPrices || hasAnyFuelCharge)) {
      try {
        setIsOffRenting(true);
        if (shouldUpdateOrderWithLateChargeSummary) {
          await updateOrder(
            order.id,
            toUpdateOrderRequest(order, customPrices ?? [], offRentRequest, lateChargeSummary!, currency!),
          );
        } else if (shouldUpdateOrderWithExistingSummary) {
          await updateOrder(
            order.id,
            toUpdateOrderRequest(order, customPrices ?? [], offRentRequest, existingSummary!, currency!),
          );
        }
        await offRentOrder(orderId, offRentRequest);

        if (inventoryReturnRequest && inventoryReturnRequest.orderProductIds.length > 0) {
          if (!waiveLateCharge && invoice) {
            await Promise.all(
              hourOverageOverrides
                ?.filter(
                  (ov) => ov.overageCharge > 0 && inventoryReturnRequest.orderProductIds.includes(ov.orderProductId),
                )
                .map((ov) => {
                  const hourOverage = hourOverages!.find(
                    (ho) => ho.orderProductId === ov.orderProductId && ho.unitId === ov.unitId,
                  )!;
                  const op = order.products.find((op) => op.id === ov.orderProductId)!;
                  const unit = op.assignedUnits.find((au) => ov.unitId === au.id)!;
                  const unitPrice = safeRound(ov.overageCharge / hourOverage.overageHours / 100, 2);
                  return createLineItem(invoice.id, {
                    name: `Hour overage ${op.product.name} - ${unit[unitTrackingType]}`,
                    description: `${hourOverage.overageHours} hours @ ${unitPrice.toFixed(2)}/hr`,
                    unitPrice,
                    quantity: hourOverage.overageHours,
                    taxable: op.product.taxable,
                  });
                }),
            );
          }
          await inventoryReturn(orderId, inventoryReturnRequest);
        }

        await onComplete(inventoryReturnRequest && inventoryReturnRequest.orderProductIds.length > 0 ? true : false);
      } catch (err: any) {
        toast(err.message, 'error');
      } finally {
        setIsOffRenting(false);
      }
    }
  }

  const { hourOverageTax, adhocLineItems, adHocLineItemsTotal, lateChargeTotal, total, isValid } = offRentCalculations({
    order,
    currency,
    hourOverageOverrides,
    inventoryReturnRequest,
    existingSummary,
    lateChargeSummary,
    waiveLateCharge,
    offRentRequest,
  });

  const cycleBillingReturnTotals = inventoryReturnCalculations({
    financialSummary: lateChargeSummary,
    order,
    currency: currency ?? 'USD',
    hourOverageOverrides,
    invoice,
    inventoryReturnRequest,
    canInventoryReturn: canOffRent,
    lateChargeTotal,
  });

  const orderValidationMessages = [...(cycleBillingReturnTotals.orderValidationMessages ?? [])];

  const isAnythingLoading = isLoading || isOffRenting || isPricesLoading || isFinancialSummaryLoading || loading;

  return (
    <Modal
      isOpen={true}
      title="Off Rent Order"
      actions={
        !order || isOffRenting || showSelectProductsStep ? null : (
          <>
            <Button className="button button-secondary" onClick={() => onCancel()}>
              Cancel
            </Button>
            <Button
              className="button button-yellow"
              disabled={!isValid || orderValidationMessages.length > 0 || isAnythingLoading}
              onClick={() => doOffRent()}
              data-cy="off-rent-button"
            >
              {isAnythingLoading ? (
                <div className="flex items-center gap-2">
                  <Spinner />
                  Calculating...
                </div>
              ) : (
                'Off Rent'
              )}
            </Button>
          </>
        )
      }
      close={() => onCancel()}
      className="off-rent-order-modal overflow-scroll w-full"
    >
      {noProductsToOffRent ? (
        <ValidationError>
          <ul>
            <li>No remaining products to off rent</li>
          </ul>
        </ValidationError>
      ) : // loading state...
      !(order && offRentRequest && configs && locations) || isOffRenting ? (
        <Spinner />
      ) : // select products/units screen
      showSelectProductsStep ? (
        <PartialOffRentStep
          order={order}
          setOrder={setOrder}
          configs={configs}
          offRentRequest={offRentRequest}
          setOffRentRequest={setOffRentRequest}
          locations={locations ?? []}
          inventoryReturnRequest={inventoryReturnRequest}
          setInventoryReturnRequest={setInventoryReturnRequest}
          onComplete={() => {
            setShowSelectProductsStep(false);
          }}
          onCancel={onCancel}
          customPrices={customPrices}
          fuelCharges={fuelCharges}
          setFuelCharges={setFuelCharges}
          isCalculating={isAnythingLoading}
        />
      ) : (
        offRentRequest && (
          <div className="confirm-off-rent">
            <div className="flex flex-column w-full gap-2">
              <OffRentHeader
                offRentRequest={offRentRequest}
                setOffRentRequest={setOffRentRequest}
                order={order}
                onOffRentUpdateDateConfiguration={onOffRentUpdateDateConfiguration}
                onOffRentUpdateDateCheckbox={onOffRentUpdateDateCheckbox}
                setOnOffRentUpdateDateCheckbox={setOnOffRentUpdateDateCheckbox}
                showInventoryReturn={showInventoryReturn}
                inventoryReturnRequest={inventoryReturnRequest}
                locations={locations}
                configs={configs}
                setWaiveLateCharge={setWaiveLateCharge}
                waiveLateCharge={waiveLateCharge}
                setInventoryReturnRequest={setInventoryReturnRequest}
              />

              <div className="w-full border-solid border-[var(--grey-300)]"></div>
              {/* -------------------- start of grid ------------------------ */}

              <div>
                <div className={`grid grid-cols-6 gap-4 text-[0.9rem] min-w-[525px]`}>
                  <OffRentGridHeader
                    order={order}
                    showInventoryReturn={showInventoryReturn}
                    inventoryReturnRequest={inventoryReturnRequest}
                    fuelCharges={fuelCharges}
                    hourOverages={hourOverages}
                  />
                  <OffRentGridProducts
                    order={order}
                    offRentRequest={offRentRequest}
                    inventoryReturnRequest={inventoryReturnRequest}
                    existingSummary={existingSummary}
                    lateChargeSummary={lateChargeSummary}
                    waiveLateCharge={waiveLateCharge}
                    currency={currency}
                    setLateChargeSummaryRequest={setLateChargeSummaryRequest}
                    unitTrackingType={unitTrackingType}
                    setInventoryReturnRequest={setInventoryReturnRequest}
                    getOverageForUnit={getOverageForUnit}
                    getOverrideForUnit={getOverrideForUnit}
                    updateOverageOverride={updateOverageOverride}
                    getFuelChargeForUnit={getFuelChargeForUnit}
                    updateFuelCharge={updateFuelCharge}
                    fuelCharges={fuelCharges}
                    isCalculating={isCalculating}
                  />

                  <OffRentAdHocAndOverrages
                    order={order}
                    adhocLineItems={adhocLineItems}
                    currency={currency}
                    hourOverageOverrides={hourOverageOverrides}
                    hourOverageTax={hourOverageTax}
                  />

                  <OffRentGridTotal
                    order={order}
                    adHocLineItemsTotal={adHocLineItemsTotal}
                    existingSummary={existingSummary}
                    waiveLateCharge={waiveLateCharge}
                    lateChargeTotal={lateChargeTotal}
                    total={total}
                    inventoryReturnRequest={inventoryReturnRequest}
                    fuelCharges={fuelCharges}
                    hourOverages={hourOverages}
                    cycleBillingReturnTotals={cycleBillingReturnTotals}
                  />
                </div>
              </div>

              <OffRentValidationError orderValidationMessages={orderValidationMessages} />
            </div>
          </div>
        )
      )}
    </Modal>
  );
};
